import type { AppMenuButtonProps } from "../../AppFlyoutAnchor/next";
import { useAppMenuButtonState } from "../../AppFlyoutAnchor/next";
import { dataUniqueId } from "@1js/acui-common";

import type { MenuButtonProps } from "@fluentui/react-components";
import { useMenuButton_unstable } from "@fluentui/react-components";
import {
  useAppComponentTooltips,
  useV9TooltipEnabled,
} from "@1js/acui-tooltip";

export const useAppSplitButtonMenuButton = (
  props: AppMenuButtonProps,
  ref: React.Ref<HTMLButtonElement>
) => {
  const { disabled, icon, location, customTooltip, tooltipDirection } = props;

  // Remove ID so it doesn't get added as an attribute on the HTML button
  const { id, ...propsWithoutId } = props;
  const v9TooltipEnabled = useV9TooltipEnabled();

  let finalRef = ref;
  if (!v9TooltipEnabled) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    finalRef = useAppComponentTooltips(ref, {
      ...props,
      direction: tooltipDirection,
    }).mergedRef;
  }

  // if customTooltip is passed in as a prop, attach a tooltip to the ref, otherwise use the default ref
  const menuButtonRef = customTooltip ? finalRef : ref;

  // 'type' override here needed so that our types (e.g. "AppButtonProps") aren't assigned to the HTML element
  const state = useMenuButton_unstable(
    {
      ...propsWithoutId,
      type: "button",
      disabledFocusable: disabled,
    } as MenuButtonProps,
    menuButtonRef
  );

  if (!icon) {
    // If we didn't provide an icon, don't render the slot at all
    // The MenuButton in the SplitButton should only have the menuIcon
    state.icon = undefined;
  }

  // We modified the appbutton to render labels based on hideLabel prop only
  // Specifically set this to true for split button menu buttons unless they
  // render their own label (like a large split button)
  const propsWithHideLabel = { ...props, hideLabel: !props.children };

  const appMenuButtonState = useAppMenuButtonState(propsWithHideLabel, state);

  // Split button primary and menu buttons both have the same unique id which is set
  // in useAppMenuButtonState. We need to override the id to have an additional identifier
  // when it is the split button menu button
  appMenuButtonState.root["data-unique-id"] = dataUniqueId(
    { id, location },
    "Menu"
  );
  return appMenuButtonState;
};
